import React from 'react';
import useAuth from "../../../hooks/useAuth";
import {
    allFilters,
    filterSingleDay,
    filterWeek,
    filterMonth,
    filterYear
} from "../../../pages/Home/FilterInfoOverview";
import {
    Box,
    Button,
    IconButton,
    useMediaQuery,
    useTheme,
    Radio,
    RadioGroup,
    FormControlLabel
} from "@mui/material";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import moment from "moment";
import 'moment/locale/fr';
import dayjs from "dayjs";
import localStrings from "../../../localStrings";
import { SingleSelect } from "../../../components/single-select";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

moment.locale('fr');

function SelectorPeriod({setOpenDatePicker}) {
    const theme = useTheme();
    const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
    const { filter, setFilter } = useAuth();

    if (!filter?.filterType || !filter.singleDay) return null;

    function moveDateBack(dayCount) {
        setFilter({
            ...filter,
            singleDay: dayjs(moment(filter.singleDay.toDate()).subtract(dayCount || 1, 'days').format())
        });
        if (!lgUp) {
            setOpenDatePicker(false);
        }
    }

    function moveDateForward(dayCount) {
        setFilter({
            ...filter,
            singleDay: dayjs(moment(filter.singleDay.toDate()).add(dayCount || 1, 'days').format())
        });
        if (!lgUp) {
            setOpenDatePicker(false);
        }
    }

    function moveDateBackOneMonth() {
        setFilter({
            ...filter,
            singleDay: dayjs(moment(filter.singleDay.toDate()).subtract(1, 'months').format())
        });
        if (!lgUp) {
            setOpenDatePicker(false);
        }
    }

    function moveDateForwardOneMonth() {
        setFilter({
            ...filter,
            singleDay: dayjs(moment(filter.singleDay.toDate()).add(1, 'months').format())
        });
        if (!lgUp) {
            setOpenDatePicker(false);
        }
    }

    function moveDateBackOneYear() {
        setFilter({
            ...filter,
            singleDay: dayjs(moment(filter.singleDay.toDate()).subtract(1, 'year').format())
        });
        if (!lgUp) {
            setOpenDatePicker(false);
        }
    }

    function moveDateForwardOneYear() {
        setFilter({
            ...filter,
            singleDay: dayjs(moment(filter.singleDay.toDate()).add(1, 'year').format())
        });
        if (!lgUp) {
            setOpenDatePicker(false);
        }
    }

    const renderControls = () => {
        const date = moment(filter?.singleDay?.toDate());
        switch (filter.filterType) {
            case filterSingleDay:
                return (
                    <>
                        <Button color="inherit" disabled>{date.format("DD/MM/YYYY")}</Button>
                        <IconButton onClick={() => moveDateBack()}><ArrowBackIosIcon /></IconButton>
                        <IconButton onClick={() => moveDateForward()}><ArrowForwardIosIcon /></IconButton>
                    </>
                );
            case filterWeek:
                return (
                    <>
                        <Button color="inherit" disabled>
                            {localStrings.formatString(localStrings.weekNumber, date.week())}
                        </Button>
                        <IconButton onClick={() => moveDateBack(7)}><ArrowBackIosIcon /></IconButton>
                        <IconButton onClick={() => moveDateForward(7)}><ArrowForwardIosIcon /></IconButton>
                    </>
                );
            case filterMonth:
                return (
                    <>
                        <Button color="inherit" disabled>{date.format('MMMM YYYY')}</Button>
                        <IconButton onClick={moveDateBackOneMonth}><ArrowBackIosIcon /></IconButton>
                        <IconButton onClick={moveDateForwardOneMonth}><ArrowForwardIosIcon /></IconButton>
                    </>
                );
            case filterYear:
                return (
                    <>
                        <Button color="inherit" disabled>{date.format('YYYY')}</Button>
                        <IconButton onClick={moveDateBackOneYear}><ArrowBackIosIcon /></IconButton>
                        <IconButton onClick={moveDateForwardOneYear}><ArrowForwardIosIcon /></IconButton>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: lgUp ? 'row' : 'column',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                p: 2,
                ...(lgUp ? {} : {
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: 1,
                    width: '100%',
                    mx: 'auto'
                })
            }}
        >
            {/* Date picker toujours affiché en haut en mobile */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    sx={{ width: lgUp ? 180 : '100%' }}
                    label={localStrings.datePicker}
                    value={dayjs(filter.singleDay)}
                    onChange={(value) => {
                        setFilter(prev => ({ ...prev, singleDay: value }));
                        if (!lgUp) {
                            setOpenDatePicker(false);
                        }
                    }}
                />
            </LocalizationProvider>

            {/* Contrôles de navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {renderControls()}
            </Box>

            {/* Choix du type de période : Select en desktop, Radio en mobile */}
            {lgUp ? (
                <SingleSelect
                    label={allFilters.find(f => f.value === filter?.filterType)?.label}
                    options={allFilters}
                    value={filter?.filterType}
                    changeCallBack={(value) => {
                        setFilter(prev => ({ ...prev, filterType: value }));
                        if (!lgUp) {
                            setOpenDatePicker(false);
                        }
                    }}
                />
            ) : (
                <RadioGroup
                    value={filter.filterType}
                    onChange={(event) => {
                        setFilter(prev => ({ ...prev, filterType: event.target.value }));
                        if (!lgUp) {
                            setOpenDatePicker(false);
                        }
                    }}
                >
                    {allFilters.map(opt => (
                        <FormControlLabel
                            key={opt.value}
                            value={opt.value}
                            control={<Radio size="small" />}
                            label={opt.label}
                        />
                    ))}
                </RadioGroup>
            )}
        </Box>
    );
}

export default SelectorPeriod;
